import { useState, useEffect, useContext, useRef } from "react";
import { ContentfulContext } from "../contexts/contentful/ContentfulContext";

export interface UseContentfulOptions {
  contentType: string;
  query?: Record<string, string | undefined>;
  include?: number;
  parser?: (data: unknown) => unknown;
}

export interface UseContentfulResult {
  data: unknown;
  error: unknown;
  fetched: boolean;
  loading: boolean;
}

// Helper function to build query options
const buildQueryOptions = (
  contentType: string,
  query?: Record<string, string | undefined>,
  include?: number,
): Record<string, unknown> => {
  const queryOptions: Record<string, unknown> = {
    content_type: contentType,
  };

  // Add query parameters, filtering out undefined values
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) {
        queryOptions[key] = value;
      }
    }
  }

  if (include !== undefined) {
    queryOptions.include = include;
  }

  return queryOptions;
};

export const useContentful = (
  options: UseContentfulOptions,
): UseContentfulResult => {
  const client = useContext(ContentfulContext);
  const [data, setData] = useState<unknown>(undefined);
  const [error, setError] = useState<unknown>(undefined);
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(true);

  // Use ref for parser function to avoid re-fetching on every render
  // when users pass inline functions
  const parserRef = useRef(options.parser);
  parserRef.current = options.parser;

  // Serialize query object to detect changes
  // Sort keys to ensure stable serialization regardless of key order
  const queryString = options.query
    ? JSON.stringify(
        Object.keys(options.query)
          .sort((a, b) => a.localeCompare(b))
          .reduce((acc: Record<string, string | undefined>, key) => {
            acc[key] = options.query![key];
            return acc;
          }, {}),
      )
    : "";

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      if (!client) {
        if (!cancelled) {
          setError(new Error("Contentful client not available"));
          setLoading(false);
          setFetched(true);
        }
        return;
      }

      if (cancelled) return;

      try {
        setLoading(true);
        setError(undefined);

        const queryOptions = buildQueryOptions(
          options.contentType,
          options.query,
          options.include,
        );

        const response = await client.getEntries(queryOptions);

        if (cancelled) return;

        const processedData = parserRef.current
          ? parserRef.current(response)
          : response;
        setData(processedData);
        setFetched(true);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;

        setError(err);
        setFetched(true);
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [client, options.contentType, queryString, options.include]); // parser excluded to prevent unnecessary re-fetches

  return {
    data,
    error,
    fetched,
    loading,
  };
};
